import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { PauseIcon, PlayIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { type AnnouncementMediaSource } from '../constants';

export type AnnouncementMediaProps = {
  className?: string;
  media: AnnouncementMediaSource;
};

// This package has no motion dependency, so read the OS setting directly.
const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const AnnouncementVideo = memo(({ src }: { src: string }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [reduceMotion] = useState(() => prefersReducedMotion());
  // Starts as playing when autoplay is expected, so the hover-only control doesn't flash in on first paint.
  const [isPlaying, setIsPlaying] = useState(!reduceMotion);

  // A browser that blocks autoplay fires no pause event, only a rejected play(), so show Play then.
  useEffect(() => {
    if (!reduceMotion) {
      videoRef.current?.play().catch(() => setIsPlaying(false));
    }
  }, [reduceMotion]);

  const handlePause = useCallback(() => setIsPlaying(false), []);
  const handlePlay = useCallback(() => setIsPlaying(true), []);

  const handleTogglePlayback = useCallback(() => {
    const video = videoRef.current;

    if (video?.paused) {
      video.play().catch(() => setIsPlaying(false));
    } else {
      video?.pause();
    }
  }, []);

  return (
    <div className="group size-full">
      <div aria-hidden className="size-full">
        <video
          autoPlay={!reduceMotion}
          className="size-full object-cover"
          loop={!reduceMotion}
          muted
          onPause={handlePause}
          onPlay={handlePlay}
          playsInline
          ref={videoRef}
          src={src}
        />
      </div>
      {/* A fixed label with aria-pressed: the chat notice is a live region, so a changing label would re-announce it. */}
      <button
        aria-label="Pause video"
        aria-pressed={!isPlaying}
        // Covers the whole video so a click or tap anywhere on it toggles playback.
        className="group/playback absolute inset-0 flex cursor-default items-center justify-center outline-none"
        data-testid="ANNOUNCEMENT_MEDIA_PLAYBACK_BUTTON"
        onClick={handleTogglePlayback}
        type="button"
      >
        <span
          className="flex size-9 items-center justify-center rounded-full ads:bg-overlay bg-black/60 ads:text-inverse text-white opacity-0 ads:ring-focus ring-white backdrop-blur-sm transition-opacity group-hover:opacity-100 group-focus-visible/playback:opacity-100 group-focus-visible/playback:ring-2 dark:bg-gray-12/60"
          data-testid="ANNOUNCEMENT_MEDIA_PLAYBACK_ICON"
        >
          {isPlaying ? <PauseIcon size="sm" /> : <PlayIcon className="ml-0.5" size="sm" />}
        </span>
      </button>
    </div>
  );
});

AnnouncementVideo.displayName = 'AnnouncementVideo';

/**
 * Cropped to 16:9 so the card's height stays stable.
 * `[aspect-ratio:16/9]`, not `aspect-video`: the Portal's @tailwindcss/aspect-ratio setup doesn't generate the latter.
 */
export const AnnouncementMedia = memo(({ className, media }: AnnouncementMediaProps) => {
  return (
    <div
      className={cn(
        'relative w-full overflow-hidden rounded-8 border ads:border-strong border-gray-6 ads:bg-subtle bg-gray-3 [aspect-ratio:16/9]',
        className,
      )}
      data-testid="ANNOUNCEMENT_MEDIA"
    >
      {media.type === 'video' ? (
        <AnnouncementVideo src={media.src} />
      ) : (
        // biome-ignore lint/performance/noImgElement: imgix already resizes and picks the format, and the Portal renders this package outside next/image.
        <img alt="" className="size-full object-cover" loading="lazy" src={media.src} />
      )}
    </div>
  );
});

AnnouncementMedia.displayName = 'AnnouncementMedia';
